/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsTextColor} from '../../../theme/theme';
import AccordionWrapper from '../../accordion/Accordion';
import {AccordionSectionHeader} from '../../accordion/current/AccordionSectionHeader';
import {ASSET_DETAILS_CARD_WIDTH, getAccordionTopBorderStyle} from './assetDetailsAccordionStyles';
import {InfoSection} from './InfoSection';
import {InfoField} from './InfoTwoColumnGrid';

export interface AdditionalInfoAccordionProps {
  assetProfileFields: InfoField[];
  vehicleDetailsFields: InfoField[];
  deviceDetailsFields: InfoField[];
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
}

const useStyles = makeStyles(() => ({
  // ION-7461: every card in the Asset Details Drawer is a fixed 456px ...
  wrapper : {
    boxSizing : 'border-box',
    width     : ASSET_DETAILS_CARD_WIDTH
  },
  // every Asset Details Drawer accordion gets this 2px teal top border to
  // separate it from the card above it, which `AccordionSectionHeader`
  // itself has no opinion on ...
  header : getAccordionTopBorderStyle(),
  // padding now comes from AccordionWrapper's own detailsRoot (current
  // theme default: 8px 16px 24px 16px) rather than being re-applied here ...
  body   : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    width         : '100%',
    gap           : '16px'
  }
}));

/**
 * AdditionalInfoAccordion renders the ION-7466 "Additional Info" accordion
 * for the Asset Details Drawer - an `AccordionSectionHeader`
 * (`@ion/dls/src/components/accordion/current/AccordionSectionHeader`)
 * wrapped by `AccordionWrapper`
 * (`@ion/dls/src/components/accordion/Accordion`), with its three field
 * groups (Asset Profile, Vehicle Details, Device Details) each rendered by
 * `InfoSection` (`@ion/dls/src/components/assetdetails/current/InfoSection`)
 * in that order - per Figma, none of the three groups has its own title,
 * only its bordered card. This component is presentational only - callers
 * (the legacy `AssetAdditionalInfoContent`/`DeviceAdditionalInfoContent`
 * data wiring is swapped in by a later integration pass) are responsible
 * for building each section's `InfoField[]` in "left column top-to-bottom,
 * then right column top-to-bottom" order, per `InfoTwoColumnGrid`.
 *
 * Vehicle Details and Device Details render their field labels in
 * `dlsTextColor('grey')` (`#6E6E6E`) rather than Asset Profile's default
 * `grey3` (`#767676`), matching the Figma export's per-section label color.
 *
 * Expansion is uncontrolled via `defaultExpanded` by default; passing
 * `expanded` and `onExpand` instead hands control to the caller, which the
 * Asset Details Drawer uses to drive its header's expand/collapse-all
 * control. Both are forwarded straight to `AccordionWrapper`.
 *
 * ```typescript
 *
 *   import {AdditionalInfoAccordion} from
 *     '@ion/dls/src/components/assetdetails/current/AdditionalInfoAccordion';
 *   ...
 *
 *   <AdditionalInfoAccordion assetProfileFields={[
 *       {label: 'Name', value: 'Vehicle 13 Vision'},
 *       {label: 'Description', value: 'There is no description listed.', isEmpty: true, clampLines: 3},
 *       {label: 'Install Date', value: 'Date format'},
 *       {label: 'Type', value: 'Vehicle'},
 *       {label: 'Category', value: 'N/A'}
 *     ]}
 *     vehicleDetailsFields={[
 *       {label: 'License', value: '1234ABC'},
 *       {label: 'Year', value: '2015'},
 *       {label: 'VIN', value: 'JTHHP5BC3F5001754'},
 *       {label: 'State', value: 'Mississippi'},
 *       {label: 'Make', value: 'Lexus'},
 *       {label: 'Model', value: 'RC'},
 *       {label: 'Engine Type', value: 'Gas'}
 *     ]}
 *     deviceDetailsFields={[
 *       {label: 'Device ID', value: '9813145'},
 *       {label: 'ESN', value: '3271267755'},
 *       {label: 'Device Type', value: 'LMU3040'},
 *       {label: 'Device Status', value: 'Enabled'}
 *     ]} />
 *
 * ```
 *
 */
export const AdditionalInfoAccordion = ({
  assetProfileFields,
  vehicleDetailsFields,
  deviceDetailsFields,
  defaultExpanded,
  expanded,
  onExpand
}: AdditionalInfoAccordionProps) => {
  const classes = useStyles();
  return (
    <div className={classes.wrapper}>
      <AccordionWrapper isCustomStyle
        className={classes.header}
        defaultExpanded={defaultExpanded}
        expanded={expanded}
        onExpand={onExpand}
        header={<AccordionSectionHeader title='ADDITIONAL INFO' />}
        details={
          <div className={classes.body}>
            {assetProfileFields.length > 0 && (
              <InfoSection fields={assetProfileFields} />
            )}
            {vehicleDetailsFields.length > 0 && (
              <InfoSection fields={vehicleDetailsFields}
                labelColor={dlsTextColor('grey')} />
            )}
            {deviceDetailsFields.length > 0 && (
              <InfoSection fields={deviceDetailsFields}
                labelColor={dlsTextColor('grey')} />
            )}
          </div>
        } />
    </div>
  );
};

export default React.memo(AdditionalInfoAccordion);
